/**
 * Canonical flight endpoint (origin or destination) for multi-provider search.
 * UI may keep free text + picklist; this shape is the stable internal contract.
 *
 * @typedef {'airport' | 'city' | 'multi_city' | 'region' | 'unknown'} FlightLocationSemanticType
 *
 * @typedef {Record<string, Record<string, unknown>>} FlightProviderRefs
 * Keys = provider id (e.g. "lds", "amadeus"). Values = opaque blobs that mappers understand.
 *
 * @typedef {Object} CanonicalFlightLocation
 * @property {string} label — default UI / API fallback (usually EN)
 * @property {string} [labelHe] — Hebrew label when available
 * @property {string} [iata] — primary IATA airport/city code when known
 * @property {string} [countryCode] — ISO 3166-1 alpha-2 when known
 * @property {FlightLocationSemanticType} type — semantic classification
 * @property {FlightProviderRefs} providerRefs — per-supplier payloads; always include `lds` when sourced from SearchContext
 * @property {string} [source] — debug: "lds_autocomplete" | "free_text" | "legacy" | etc.
 */

import {
  primaryLocation as primaryFromRaw,
  resolveFlightEndpointToken,
} from "../utils/flightSearchPayloadHelpers";

const HEBREW_CHAR_RE = /[\u0590-\u05FF]/;

/** @param {Record<string, unknown>|undefined} lds */
function ldsCityCodeToken(lds) {
  if (!lds || typeof lds !== "object") return undefined;
  const cc = lds.CITYCODE;
  if (cc == null || String(cc).trim() === "") return undefined;
  const s = String(cc).trim();
  if (/^[A-Za-z0-9]{2,4}$/.test(s)) return s.toUpperCase();
  return undefined;
}

/** @type {FlightLocationSemanticType} */
function inferType(obj) {
  if (!obj || typeof obj !== "object") return "unknown";
  const code = obj.AIRPORTCODE ?? obj.IATACODE ?? obj.iata ?? obj.airportCode;
  if (code != null && String(code).trim() !== "") return "airport";
  if (obj.CITYCODE != null && String(obj.CITYCODE).trim() !== "") return "city";
  if (obj.CITYNAME || obj.city || obj.fullname) return "city";
  return "unknown";
}

/**
 * Normalize whatever SearchContext stores as `origin`, `location`, or array[0] thereof.
 * Does not mutate SearchContext — pure read of `raw`.
 *
 * @param {unknown} raw — object (LDS autocomplete shape), string, or array of those
 * @returns {CanonicalFlightLocation | null}
 */
export function fromSearchContextLocation(raw) {
  if (raw == null) return null;
  const primary = primaryFromRaw(raw);
  if (primary == null) return null;

  if (typeof primary === "string") {
    const t = primary.trim();
    if (!t) return null;
    const looksLikeCode = /^[A-Za-z0-9]{2,4}$/.test(t);
    return {
      label: t,
      labelHe: undefined,
      iata: looksLikeCode ? t.toUpperCase() : undefined,
      countryCode: undefined,
      type: looksLikeCode ? "airport" : "unknown",
      providerRefs: { lds: { freeText: t } },
      source: looksLikeCode ? "iata_string" : "free_text",
    };
  }

  if (typeof primary !== "object") return null;

  const token = resolveFlightEndpointToken(primary);
  const label =
    primary.CITYNAME ||
    primary.city ||
    primary.name ||
    primary.fullname ||
    token ||
    "";
  const labelHe =
    primary.CITYNAME_HEB ||
    primary.COUNTRYNAME_HEB ||
    undefined;
  const iata =
    (primary.AIRPORTCODE && String(primary.AIRPORTCODE).trim()) ||
    (primary.IATACODE && String(primary.IATACODE).trim()) ||
    (primary.CITYCODE && String(primary.CITYCODE).trim()) ||
    (token && /^[A-Za-z0-9]{2,4}$/.test(token) ? token : undefined) ||
    undefined;
  const countryCode =
    (primary.COUNTRYCODE && String(primary.COUNTRYCODE).trim()) || undefined;

  return {
    label: label || token || "—",
    labelHe,
    iata,
    countryCode,
    type: inferType(primary),
    providerRefs: {
      lds: { ...primary },
    },
    source: "legacy",
  };
}

/**
 * String token for LDS flight search (depOrigCities / depDestCities style).
 * Today this mirrors `resolveFlightEndpointToken` / `resolveDestinationTokenForSearchFlights` behavior.
 *
 * @param {CanonicalFlightLocation | null | undefined} canonical
 * @returns {string | undefined}
 */
export function toLdsLocationCode(canonical) {
  if (!canonical) return undefined;
  const lds = canonical.providerRefs?.lds;
  if (lds && typeof lds === "object") {
    const fromLds = resolveFlightEndpointToken(lds);
    if (fromLds) return fromLds;
    const cityCode = ldsCityCodeToken(lds);
    if (cityCode) return cityCode;
    if (typeof lds.freeText === "string" && lds.freeText.trim()) {
      const ft = lds.freeText.trim();
      if (/^[A-Za-z0-9]{2,4}$/.test(ft)) return ft.toUpperCase();
      if (!HEBREW_CHAR_RE.test(ft)) return ft;
      return undefined;
    }
  }
  if (canonical.iata && String(canonical.iata).trim()) {
    return String(canonical.iata).trim().toUpperCase();
  }
  if (canonical.label && String(canonical.label).trim()) {
    const lab = String(canonical.label).trim();
    if (HEBREW_CHAR_RE.test(lab)) return undefined;
    return lab;
  }
  return undefined;
}

/**
 * Future: provider-specific mappers receive `CanonicalFlightLocation` and build their API body.
 * UI and SearchContext stay on legacy shapes until a deliberate migration.
 *
 * @typedef {(c: CanonicalFlightLocation) => unknown} FlightProviderMapper
 */

/** @type {Record<string, FlightProviderMapper>} */
const providerMappers = {};

export function registerFlightProviderMapper(providerId, mapper) {
  if (!providerId || typeof mapper !== "function") return;
  providerMappers[providerId] = mapper;
}

export function mapCanonicalToProvider(providerId, canonical) {
  const fn = providerMappers[providerId];
  if (!fn || !canonical) return undefined;
  return fn(canonical);
}
